import { useEffect, useRef, useState } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { triggerHaptic } from "@/lib/nativeHaptics";

interface ImageItem {
  src: string;
  alt?: string;
}

interface Props {
  images: ImageItem[];
  initialIndex?: number;
  onClose: () => void;
  onSwipeIndexChange?: (from: number, to: number) => void;
}

/**
 * Fullscreen image viewer with pinch-zoom (react-zoom-pan-pinch),
 * swipe-down-to-dismiss, ESC + backdrop tap + × button close.
 * Supports multi-image horizontal swipe when images.length > 1.
 */
export function EnhancedImageViewer({
  images,
  initialIndex = 0,
  onClose,
  onSwipeIndexChange,
}: Props) {
  const [index, setIndex] = useState(initialIndex);
  const touchStart = useRef<{ x: number; y: number; t: number } | null>(null);
  const dismissedRef = useRef(false);

  const safeClose = () => {
    if (dismissedRef.current) return;
    dismissedRef.current = true;
    void triggerHaptic("selection");
    onClose();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") safeClose();
      else if (e.key === "ArrowRight") goto(index + 1);
      else if (e.key === "ArrowLeft") goto(index - 1);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const goto = (next: number) => {
    if (images.length <= 1) return;
    const clamped = Math.max(0, Math.min(images.length - 1, next));
    if (clamped === index) return;
    onSwipeIndexChange?.(index, clamped);
    setIndex(clamped);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY, t: Date.now() };
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    const adx = Math.abs(dx);
    const ady = Math.abs(dy);
    // Swipe down to dismiss
    if (dy > 100 && ady > adx) {
      safeClose();
      return;
    }
    // Horizontal swipe to navigate (only when multi-image)
    if (images.length > 1 && adx > 60 && adx > ady) {
      goto(index + (dx < 0 ? 1 : -1));
    }
  };

  const current = images[index];
  if (!current) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      onClick={(e) => {
        // Only close on direct backdrop tap (not bubbled from image)
        if (e.target === e.currentTarget) safeClose();
      }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.92)",
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        touchAction: "none",
      }}
    >
      {/* Close button */}
      <button
        type="button"
        onClick={safeClose}
        aria-label="Close image viewer"
        style={{
          position: "absolute",
          top: "calc(env(safe-area-inset-top, 0px) + 14px)",
          right: 14,
          width: 40,
          height: 40,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.15)",
          border: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontSize: 18,
          cursor: "pointer",
          zIndex: 2,
        }}
      >
        <i className="ti ti-x" />
      </button>

      {/* Counter when multi-image */}
      {images.length > 1 && (
        <div
          style={{
            position: "absolute",
            top: "calc(env(safe-area-inset-top, 0px) + 22px)",
            left: 0,
            right: 0,
            textAlign: "center",
            color: "rgba(255,255,255,0.7)",
            fontSize: 12,
            pointerEvents: "none",
          }}
        >
          {index + 1} / {images.length}
        </div>
      )}

      <TransformWrapper
        key={index}
        minScale={1}
        maxScale={4}
        doubleClick={{ mode: "toggle", step: 1.5 }}
        wheel={{ step: 0.2 }}
      >
        <TransformComponent
          wrapperStyle={{ width: "100%", height: "100%" }}
          contentStyle={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={current.src}
            alt={current.alt ?? ""}
            style={{
              maxWidth: "100vw",
              maxHeight: "100vh",
              objectFit: "contain",
              userSelect: "none",
              WebkitUserSelect: "none",
              WebkitTouchCallout: "none",
            }}
            draggable={false}
          />
        </TransformComponent>
      </TransformWrapper>
    </div>
  );
}
